// DOC-1: build the /bulk-upload/{sid}/confirm body from the session files.
// Moved out of BulkUploadWizard.handleConfirm unchanged in behaviour so it can
// be tested: every uploaded file is listed; extraction data is attached only
// for suggestions the user accepted or edited.
export function buildConfirmedFiles(sessionFiles, suggestions) {
  const acceptedByFileId = {};
  (suggestions || []).forEach((s) => {
    if (s.status === "accepted" || s.status === "edited") {
      acceptedByFileId[s.file_id] = {
        status: s.status,
        extraction_type: s.extraction_type,
        data: s.data,
        summary: s.summary,
      };
    }
  });

  return (sessionFiles || []).map((f) => ({
    file_id: f.file_id,
    category: f.user_category || f.ai_category || "other",
    title: f.user_title || f.ai_title || f.filename,
    description: "",
    // Phase 2: attach extraction data if accepted
    extraction: acceptedByFileId[f.file_id] || null,
  }));
}

const KIND = {
  blueprints: ["a", "Blueprint"],
  permits: ["a", "Permit"],
  contracts: ["a", "Contract"],
  quotes: ["a", "Quote"],
  invoices: ["an", "Invoice"],
  warranties: ["a", "Warranty"],
  photos: ["a", "Photo"],
  correspondence: ["a", "Correspondence"],
  schedules: ["a", "Schedule"],
};

const FIELD_LABEL = {
  contract_price: "Contract price",
  budget: "Budget",
  bill: "Bill",
  expense: "Expense",
  quote: "Quote",
  contact: "Contact",
  permit: "Permit",
  warranty: "Warranty",
  draft_change_order: "Draft change order",
  signed_change_order: "Signed change order",
  draws: "Draws",
  selection: "Selection",
};

const CHOICE_TOOLS = new Set(["draft_change_order", "record_signed_change_order"]);

export const EDITABLE_KEYS = {
  add_bills: ["amount", "vendor", "description", "date", "invoice_number"],
  add_expense: ["amount", "vendor", "description", "date", "subcategory"],
  update_project_budget: ["new_budget"],
  set_contract_value: ["value"],
  add_contact: ["name", "company", "phone", "email", "notes"],
  draft_change_order: ["title", "description", "amount", "date"],
  record_signed_change_order: ["title", "description", "amount", "date", "signed_date", "signed_by_text"],
  add_selection: ["title", "room", "category", "description"],
};

export function editableKeys(tool, card) {
  const keys = EDITABLE_KEYS[tool] || [];
  if (card && card.creating === false) return keys.filter((key) => key !== "amount");
  return keys;
}

export function suggestionCopy(category) {
  const pair = KIND[category];
  if (!pair) return "We could not tell what this file is. Use the category list if that is wrong.";
  return `We think this file is ${pair[0]} ${pair[1]}. Use the category list if that is wrong.`;
}

const CARD_FIELD_LABEL = {
  signed_by_text: "Who signed it, as written",
  signed_date: "Date signed",
  work_started_date: "Work start date",
  invoice_number: "Invoice number",
  new_budget: "New budget",
  value: "Contract price",
};

export function cardFieldLabel(key) {
  const known = CARD_FIELD_LABEL[key];
  if (known) return known;
  const words = String(key || "").split("_").filter(Boolean);
  if (!words.length) return "Value";
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}

export function signedCardGaps(card) {
  if (!card || card.status !== "pending" || card.tool !== "record_signed_change_order") return [];
  const source = card.edits && typeof card.edits === "object" ? card.edits : (card.values || {});
  const gaps = [];
  if (!String(source.signed_by_text || "").trim()) gaps.push("Who signed it");
  if (!String(source.signed_date || "").trim()) gaps.push("Date signed");
  return gaps;
}

export function cardActionCopy() {
  return "Confirm saves this card. Edit changes the values first. Cancel leaves it out. Accept all saves only the cards that do not need their own choice. Skip all leaves the pending cards out.";
}

export function isInfoCard(card) {
  return Boolean(card && (card.info === true || card.tool === "info"));
}

export function cardNeedsChoice(card) {
  if (!card || card.status !== "pending") return false;
  if (isInfoCard(card)) return true;
  if (card.duplicate || card.result === "possible_duplicate") return true;
  if (signedCardGaps(card).length) return true;
  return CHOICE_TOOLS.has(card.tool);
}

export function fieldLabel(field) {
  return FIELD_LABEL[field] || "Value";
}

export function reviewFromConfirm(response, sessionFiles) {
  const body = response && typeof response === "object" ? response : {};
  const byDoc = {};
  (Array.isArray(body.created) ? body.created : []).forEach((row) => {
    if (row && row.document_id) byDoc[row.document_id] = row;
  });
  const byFile = {};
  (sessionFiles || []).forEach((file) => {
    if (file && file.file_id) byFile[file.file_id] = file;
  });
  const seenApplied = {};
  const applied = [];
  (Array.isArray(body.applied) ? body.applied : []).forEach((row) => {
    if (!row || typeof row !== "object" || row.applied === false) return;
    const key = `${row.document_id || ""}|${row.field || ""}`;
    if (seenApplied[key]) return;
    seenApplied[key] = true;
    const doc = byDoc[row.document_id] || {};
    const file = byFile[doc.file_id] || {};
    applied.push({
      documentId: row.document_id || "",
      field: row.field || "",
      label: fieldLabel(row.field),
      filename: file.filename || doc.title || "uploaded file",
    });
  });
  const seenCards = {};
  const cards = [];
  const listed = Array.isArray(body.cards) ? body.cards : (body.card_ids || []).map((id) => ({ card_id: id }));
  listed.forEach((row) => {
    const id = typeof row === "string" ? row : (row && (row.card_id || row.actionId));
    if (!id || seenCards[id]) return;
    seenCards[id] = true;
    const item = row && typeof row === "object" ? row : {};
    const info = item.tool === "info" || item.info === true;
    cards.push({
      actionId: id,
      status: "pending",
      tool: item.tool || "",
      description: item.description || "",
      values: item.values || {},
      filename: item.filename || "",
      duplicate: item.duplicate || null,
      creating: item.creating === false ? false : undefined,
      info: info || undefined,
      edits: null,
    });
  });
  return { applied, cards };
}

export function mergeUploadCards(current, incoming) {
  const cards = [];
  const seen = {};
  const take = (card) => {
    if (!card || !card.actionId || seen[card.actionId]) return;
    seen[card.actionId] = true;
    cards.push(card);
  };
  (current || []).forEach(take);
  (incoming || []).forEach((row) => {
    if (!row || typeof row !== "object") return;
    if (row.actionId) {
      take(row);
      return;
    }
    const id = row.card_id || row.action_id;
    if (!id || seen[id]) return;
    seen[id] = true;
    const info = row.tool === "info" || row.info === true;
    cards.push({
      actionId: id,
      status: "pending",
      tool: row.tool || "",
      description: row.description || "",
      values: row.values && typeof row.values === "object" ? row.values : {},
      filename: row.filename || "",
      duplicate: row.duplicate || null,
      creating: row.creating === false ? false : undefined,
      info: info || undefined,
      edits: null,
    });
  });
  return cards;
}
